import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import moment from "moment";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Backdrop,
  Button,
  IconButton,
  Modal,
  Link,
  TextField,
  Tooltip,
  ButtonBase,
  CircularProgress,
  useTheme,
} from "@mui/material";
import ExternalLink from "../../components/utilities/ExternalLink";
import ExternalButtonBase from "../../components/utilities/ExternalButtonBase";
import { useEmbeddedExitConfirm } from "../../hooks/useEmbeddedExitConfirm";
import EmbeddedExitConfirmDialog from "../../components/modals/EmbeddedExitConfirmDialog";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import { Formik } from "formik";
import * as Yup from "yup";
import ShareModal from "../../components/modals/ShareModal";
import EventCard from "../../components/layout/EventCard";
import { globalVariables } from "../../global/GlobalVariables";
import {
  BCPCalendarDay,
  BCPGlobe,
  BCPShareNodes,
  BCPShop,
  BCPTrophy,
  BCPXmark,
} from "../../components/icons/bcp/BCPIcons";
import { getData, postData } from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function OrganizationLanding(props) {
  let { organizationId } = useParams();
  const theme = useTheme();
  const authContext = useContext(AuthContext);

  const {
    dialogOpen,
    handleConfirm,
    handleCancel,
    createClickHandler,
    brandName,
    storagePrefix,
  } = useEmbeddedExitConfirm();

  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState(props?.organization || null);
  const [events, setEvents] = useState(null);

  const [contactOrganizationModal, setContactOrganizationModal] =
    useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      try {
        if (organizationId) {
          const apiStore = organization
            ? organization
            : await getData(`/gamestores/${organizationId}`, {
                queryStringParameters: {
                  "expand[]": ["location"],
                },
              });

          if (apiStore.photoFileId) {
            apiStore.photo = await getData(`/files/${apiStore.photoFileId}`, {
              queryStringParameters: {},
            });
          }

          if (apiStore.websiteURL) {
            if (!/^https?:\/\//i.test(apiStore.websiteURL)) {
              apiStore.websiteURL = "http://" + apiStore.websiteURL;
            }
          }

          setOrganization(apiStore);
          setLoading(false);

          let queryStringParameters = {
            limit: 6,
            gameStoreId: organizationId,
            startDate: `${moment()
              .subtract(3, "days")
              .format("YYYY-MM-DD")}T00:00:00Z`,
            endDate: `${moment()
              .add(2, "weeks")
              .format("YYYY-MM-DD")}T00:00:00Z`,
            "expand[]": ["gameSystem"],
          };
          if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
            queryStringParameters.gameSystemId =
              getEnvironmentVariable("SINGLE_GAME_SYSTEM");
          } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
            queryStringParameters.gameType =
              getEnvironmentVariable("SINGLE_GAME_TYPE");
          } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
            queryStringParameters.gameSystemManufacturer =
              getEnvironmentVariable("SINGLE_GAME_MANUFACTURER");
          }

          var apiEvents = await getData("/events", {
            queryStringParameters: queryStringParameters,
          });
          apiEvents = apiEvents.data;
          apiEvents.forEach((event) => {
            if (event.pricingDict) {
              var keys = Object.keys(event.pricingDict);
              if (keys.length > 0) {
                event.pricingInfo = {
                  currency: keys[0],
                  amount: (event.pricingDict[keys[0]] / 100).toFixed(2),
                };
              }
            }
          });
          setEvents(apiEvents);
        }
      } catch (err) {
        postNotification("error", err);
        setLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}
      {organization && (
        <Box sx={{ width: "100%" }}>
          <Container sx={{ pb: 2 }} maxWidth="lg">
            <Box
              sx={{
                height: ["400px", "300px"],
                maxHeight: ["400px", "300px"],
                minHeight: ["400px", "300px"],
                overflow: "hidden",
                position: "absolute",
                // top: "61px",
                left: 0,
                width: "100%",
                zIndex: "-1",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "0",
                  left: "0",
                  width: "100%",
                }}
              >
                <picture
                  content={
                    organization?.photo?.url ||
                    organization.imageURL ||
                    `${getEnvironmentVariable(
                      "SYSTEM_IMAGES_CLOUDFRONT"
                    )}/images/EventImage${
                      theme?.palette?.mode === "dark" ? "Dark" : ""
                    }.png`
                  }
                >
                  <source
                    srcset={
                      organization?.photo?.url ||
                      organization.imageURL ||
                      `${getEnvironmentVariable(
                        "SYSTEM_IMAGES_CLOUDFRONT"
                      )}/images/EventImage${
                        theme?.palette?.mode === "dark" ? "Dark" : ""
                      }.png`
                    }
                    sizes="100vw"
                  />
                  <img
                    data-automation="listing-hero-image"
                    alt=""
                    style={{
                      transform: "translate(-50%, -50%)",
                      filter: "blur(50px) brightness(.9)",
                      display: "inline-block",
                      width: "200%",
                      position: "relative",
                      top: "50%",
                      left: "50%",
                    }}
                  />
                </picture>
              </Box>
            </Box>
            <Grid container sx={{ gap: 2 }}>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12} sm={7}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        maxWidth: "100%",
                        minWidth: "100%",
                        paddingTop: "0px !important",
                        backgroundColor: "rgb(46 55 77 / 44%);",
                        "&:hover": {
                          opacity: [0.9, 0.8, 0.7],
                        },
                      }}
                    >
                      <img
                        src={
                          organization?.photo?.url ||
                          organization.imageURL ||
                          `${getEnvironmentVariable(
                            "SYSTEM_IMAGES_CLOUDFRONT"
                          )}/images/EventImage${
                            theme?.palette?.mode === "dark" ? "Dark" : ""
                          }.png`
                        }
                        alt={organization.name}
                        style={{ maxWidth: "100%", maxHeight: 300 }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "end",
                        position: "relative",
                        top: "-16px",
                        pr: 2,
                      }}
                    >
                      <Button
                        color="primary"
                        variant="outlined"
                        sx={{
                          backgroundColor: "background.secondary",
                          "&:hover": { backgroundColor: "background.paper" },
                          minWidth: "unset",
                          padding: "8px 8px",
                          borderRadius: "16px",
                        }}
                        onClick={() => setShareModalOpen(true)}
                      >
                        <BCPShareNodes />
                      </Button>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={5}
                    sx={{
                      ml: [2],
                      mr: [-2],
                      display: "flex",
                      alignItems: "center",
                      maxWidth: [
                        "calc(100% - 36px) !important",
                        "calc(41.666667% - 36px) !important",
                      ],
                      minWidth: [
                        "calc(100% - 36px) !important",
                        "calc(41.666667% - 36px) !important",
                      ],
                      "& .MuiTypography-root": {
                        overflowWrap: "break-word",
                        wordWrap: "break-word",
                      },
                    }}
                  >
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                          {organization.name}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography gutterBottom>
                          <Box
                            sx={{
                              minWidth: "35px",
                              verticalAlign: "bottom",
                              display: "inline-flex",
                            }}
                          >
                            <Tooltip title="Contact Organization">
                              <BCPShop
                                sx={{
                                  color: theme?.palette?.text?.secondary,
                                  verticalAlign: "bottom",
                                }}
                              />
                            </Tooltip>
                          </Box>
                          <Link
                            underline="none"
                            href="#"
                            onClick={(e) => {
                              setContactOrganizationModal(true);
                            }}
                          >
                            Contact Organization
                          </Link>
                        </Typography>
                      </Grid>

                      {Boolean(organization.websiteURL) &&
                        organization.websiteURL !== "" && (
                          <Grid item xs={12}>
                            <Typography gutterBottom>
                              <Box
                                sx={{
                                  minWidth: "35px",
                                  verticalAlign: "bottom",
                                  display: "inline-flex",
                                }}
                              >
                                <Tooltip title="External Link">
                                  <BCPGlobe
                                    sx={{
                                      color: theme?.palette?.text?.secondary,
                                      verticalAlign: "bottom",
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                              <Link
                                underline="none"
                                href={organization.websiteURL}
                                target="_blank"
                              >
                                External Link
                              </Link>
                            </Typography>
                          </Grid>
                        )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                xs={12}
                sm={7}
                sx={{
                  p: 2,
                }}
                order={{ xs: 2, sm: 1 }}
              >
                <Grid container spacing={1}>
                  {!getEnvironmentVariable("HIDE_RANKINGS") && (
                    <Grid item xs={12} sm={6} sx={{ p: 2 }}>
                      <ExternalButtonBase
                        sx={{
                          width: "100%",
                          display: "block",
                          borderColor: "divider",

                          p: 1,
                          textAlign: "center",
                          border: "1px solid rgba(0, 0, 0, 0.12)",
                          borderRadius: 1,
                          boxShadow: 1,
                          backgroundColor: "background.secondary",

                          "&:hover": {
                            backgroundColor: "rgb(46 55 77 / 24%);",
                          },
                        }}
                        href={`/organization/rankings/${organizationId}`}
                        target="_blank"
                      >
                        <Typography gutterBottom>
                          <BCPTrophy sx={{ fontSize: 50 }} />
                        </Typography>
                        <Typography variant="h3" gutterBottom>
                          Rankings
                        </Typography>
                      </ExternalButtonBase>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} sx={{ p: 2 }}>
                    <ExternalButtonBase
                      sx={{
                        width: "100%",
                        display: "block",
                        borderColor: "divider",

                        p: 1,
                        textAlign: "center",
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: "background.secondary",

                        "&:hover": {
                          backgroundColor: "rgb(46 55 77 / 24%);",
                        },
                      }}
                      href={`/organization/events/${organizationId}`}
                      target="_blank"
                    >
                      <Typography gutterBottom>
                        <BCPCalendarDay sx={{ fontSize: 50 }} />
                      </Typography>
                      <Typography variant="h3" gutterBottom>
                        Calendar
                      </Typography>
                    </ExternalButtonBase>
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                xs={12}
                sm={5}
                sx={{
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 1,
                  backgroundColor: "background.secondary",
                  maxWidth: ["100%", "calc(41.666667% - 16px)"], // deal with spacing bug
                  p: 2,
                }}
                order={{ xs: 1, sm: 2 }}
              >
                {Boolean(organization.location) && (
                  <>
                    <Typography gutterBottom>Location</Typography>
                    <ExternalLink
                      underline="none"
                      href={`https://google.com/maps/place/${
                        organization.location?.streetNum || ""
                      } ${organization.location?.streetName || ""} ${
                        organization.location?.city || ""
                      } ${organization.location?.zip || ""} ${
                        organization.location?.state || ""
                      } ${organization.location?.country || ""}`.trim()}
                      target="_blank"
                    >
                      {organization.name && (
                        <Typography>{organization.name}</Typography>
                      )}

                      {Boolean(
                        organization.location?.streetNum ||
                          organization.location?.streetName
                      ) && (
                        <Typography>
                          {`${organization.location?.streetNum || ""} ${
                            organization.location?.streetName
                          }`.trim()}
                        </Typography>
                      )}
                      {Boolean(
                        organization.location?.city ||
                          organization.location?.zip ||
                          organization.location?.state
                      ) && (
                        <Typography>
                          {`${
                            organization.location?.city
                              ? `${organization.location?.city}, `
                              : ""
                          }${organization.location?.state} ${
                            organization.location?.zip
                          }`.trim()}
                        </Typography>
                      )}
                      {Boolean(organization.location?.country) && (
                        <Typography>
                          {organization.location?.country}
                        </Typography>
                      )}
                    </ExternalLink>
                  </>
                )}
              </Grid>
            </Grid>
            <Grid container sx={{ mt: 2 }}>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                }}
                order={{ xs: 2, sm: 1 }}
              >
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="h4" gutterBottom>
                      Upcoming Events
                    </Typography>
                  </Grid>
                  {!events && (
                    <Grid item xs={12}>
                      <CircularProgress />
                    </Grid>
                  )}
                  {events && events.length > 0 && (
                    <>
                      {events.map((event, index) => {
                        return (
                          <Grid key={index} item xs={12} sm={6} md={4}>
                            <EventCard
                              event={event}
                              key={index}
                              authContext={authContext}
                            />
                          </Grid>
                        );
                      })}
                    </>
                  )}
                  <Grid item xs={12} textAlign="center">
                    <Button
                      href={`/organization/events/${organizationId}`}
                      variant="outlined"
                      color="primary"
                      onClick={createClickHandler(
                        `/organization/events/${organizationId}`,
                        "_blank"
                      )}
                      target="_blank"
                    >
                      See More
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
          <ContactOrganizationModal
            open={contactOrganizationModal}
            setOpen={setContactOrganizationModal}
            authContext={authContext}
            organization={organization}
          />
          <ShareModal
            parentOpen={shareModalOpen}
            setParentOpen={setShareModalOpen}
            url={`${window.location.origin}/organization/${organizationId}`}
            shareOptions={{
              twitter: `http://twitter.com/share?text=Checkout ${
                organization?.name
              }${
                getEnvironmentVariable("COMPANY_NAME") === "Best Coast Pairings"
                  ? " @BCPairings"
                  : ""
              }&url=${
                window.location.origin
              }/organization/${organizationId}&hashtags=${getEnvironmentVariable(
                "COMPANY_NAME"
              )
                .split(" ")
                .join("")}`,
              email: `mailto:?subject=Checkout ${
                organization?.name
              } at ${getEnvironmentVariable("COMPANY_NAME")}&body=Checkout ${
                organization?.name
              } at ${getEnvironmentVariable("COMPANY_NAME")} ${
                window.location.origin
              }/organization/${organizationId}`,
            }}
          />
        </Box>
      )}
      <EmbeddedExitConfirmDialog
        open={dialogOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        brandName={brandName}
        storagePrefix={storagePrefix}
      />
    </>
  );
}
export function ContactOrganizationModal(props) {
  const { open, setOpen, organization, authContext } = props;
  const [loading, setLoading] = useState(false);

  const globalVars = globalVariables();

  return (
    <>
      <Modal
        disableAutoFocus
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{ zIndex: 99990 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={1}
            sx={{
              mb: 1,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                Contact Organization
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              messageSubject: `${
                organization?.name ? `${organization.name} - ` : ""
              }${globalVars.site?.title}`,
              messageBody: "",
              fromName: `${
                authContext?.authState?.identity?.firstName ||
                authContext?.authState?.identity?.given_name ||
                ""
              } ${
                authContext?.authState?.identity?.lastName ||
                authContext?.authState?.identity?.family_name ||
                ""
              }`.trim(),
              fromEmail: authContext?.authState?.identity?.email || "",
            }}
            validationSchema={Yup.object().shape({
              fromName: Yup.string().max(255).required("Required"),
              fromEmail: Yup.string()
                .max(128)
                .email("Must be a valid email")
                .required("Required"),
              messageSubject: Yup.string().max(70).required("Required"),
              messageBody: Yup.string().required("Required"),
            })}
            onSubmit={async (values) => {
              setLoading(true);
              try {
                await postData(
                  `/gamestores/${organization?.id}/contactOrganization`,
                  {
                    body: values,
                  }
                );
                setLoading(false);
                postNotification("success", "Email sent!");
              } catch (err) {
                postNotification("error", err);
              }
              setLoading(false);

              return;
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="fromName"
                      label="Name"
                      value={values.fromName}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.fromName && errors.fromName)}
                      helperText={touched.fromName && errors.fromName}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="fromEmail"
                      label="Email"
                      value={values.fromEmail}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        const trimmedString = e.target.value
                          .trim()
                          .toLowerCase();
                        setFieldValue("fromEmail", trimmedString);
                      }}
                      error={Boolean(touched.fromEmail && errors.fromEmail)}
                      helperText={touched.fromEmail && errors.fromEmail}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="messageSubject"
                      label="Subject"
                      value={values.messageSubject}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(
                        touched.messageSubject && errors.messageSubject
                      )}
                      helperText={
                        touched.messageSubject && errors.messageSubject
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="messageBody"
                      label="Body"
                      value={values.messageBody}
                      variant="outlined"
                      fullWidth
                      multiline
                      required
                      rows={5}
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.messageBody && errors.messageBody)}
                      helperText={touched.messageBody && errors.messageBody}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      color="error"
                      fullWidth
                      variant="outlined"
                      onClick={(e) => {
                        setOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      color="success"
                      fullWidth
                      variant="contained"
                      type="submit"
                    >
                      Send
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
      {loading && <ModalLoadingFull />}
    </>
  );
}
